import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { unwrap } from '@billda/api';
import { labourItemsKeys } from './useLabourItemsQuery';
import { api } from '../../../shared/api/billdaApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type {
  AllocationListItem,
  LabourItemListItem,
  PlotListItem,
  PlotVariationListItem,
} from '@billda/api';

interface UseHierarchicalTableDataParams {
  siteId: number;
  plots: PlotListItem[];
  expandedPlots: Set<number>;
}

/**
 * One plot plus whatever of its tree has arrived. `undefined` collections mean
 * "still loading"; an empty array means "loaded, nothing there". The ledger
 * renders a row from the item or variation as soon as it has it and fills in
 * ref/who/when when the allocation query resolves.
 */
export interface PlotLedgerSource {
  plot: PlotListItem;
  isExpanded: boolean;
  labourItems?: LabourItemListItem[];
  variations?: PlotVariationListItem[];
  allocationsByLabourItem: ReadonlyMap<number, AllocationListItem[]>;
  allocationsByVariation: ReadonlyMap<string, AllocationListItem[]>;
}

interface UseHierarchicalTableDataReturn {
  plotsWithNested: PlotLedgerSource[];
  isLoadingAnyNested: boolean;
}

// Query keys for variations
export const variationsKeys = {
  all: ['variations'] as const,
  lists: () => [...variationsKeys.all, 'list'] as const,
  list: (siteId: number, plotId: number, isAuthenticated: boolean) =>
    [...variationsKeys.lists(), { siteId, plotId, isAuthenticated }] as const,
};

// Separate query keys for table allocations (uses regular queries, not infinite)
const tableAllocationsKeys = {
  all: ['tableAllocations'] as const,
  lists: () => [...tableAllocationsKeys.all, 'list'] as const,
  list: (
    siteId: number,
    plotId: number,
    labourItemId: number,
    isAuthenticated: boolean
  ) =>
    [
      ...tableAllocationsKeys.lists(),
      { siteId, plotId, labourItemId, isAuthenticated },
    ] as const,
  variationList: (
    siteId: number,
    plotId: number,
    variationId: string,
    isAuthenticated: boolean
  ) =>
    [
      ...tableAllocationsKeys.lists(),
      { siteId, plotId, variationId, isAuthenticated },
    ] as const,
};

const PAGE_SIZE = 100;

/**
 * Fetches the plot tree one expanded plot at a time: labour items and
 * variations per plot, then allocations per booked item and per variation.
 */
export function useHierarchicalTableData({
  siteId,
  plots,
  expandedPlots,
}: UseHierarchicalTableDataParams): UseHierarchicalTableDataReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const plotIds = useMemo(() => Array.from(expandedPlots), [expandedPlots]);

  const labourItemQueries = useQueries({
    queries: plotIds.map(plotId => ({
      queryKey: labourItemsKeys.list(siteId, plotId, isAuthenticated),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const result = unwrap(
          await api.legacy.GET(
            '/api/v2/sites/{siteId}/plots/{plotId}/labour-items',
            {
              params: {
                path: { siteId, plotId },
                query: { offset: 0, take: PAGE_SIZE },
              },
              signal,
            }
          )
        );
        return { plotId, items: result.items ?? [] };
      },
      enabled: isAuthenticated && siteId > 0,
      staleTime: 30000,
    })),
  });

  const variationQueries = useQueries({
    queries: plotIds.map(plotId => ({
      queryKey: variationsKeys.list(siteId, plotId, isAuthenticated),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const result = unwrap(
          await api.legacy.GET(
            '/api/v1/sites/{siteId}/plots/{plotId}/variations',
            {
              params: {
                path: { siteId, plotId },
                query: { page: 1, pageSize: PAGE_SIZE },
              },
              signal,
            }
          )
        );
        return { plotId, items: result.items ?? [] };
      },
      enabled: isAuthenticated && siteId > 0,
      staleTime: 30000,
    })),
  });

  const labourItemsMap = useMemo(() => {
    const map = new Map<number, LabourItemListItem[]>();
    labourItemQueries.forEach(query => {
      if (query.data) {
        map.set(query.data.plotId, query.data.items);
      }
    });
    return map;
  }, [labourItemQueries]);

  const variationsMap = useMemo(() => {
    const map = new Map<number, PlotVariationListItem[]>();
    variationQueries.forEach(query => {
      if (query.data) {
        map.set(query.data.plotId, query.data.items);
      }
    });
    return map;
  }, [variationQueries]);

  // Every booked item in an expanded plot needs its allocations: the ledger
  // merges item and booking into one row, so there is no second expand step.
  const bookedItemsWithContext = useMemo(() => {
    const items: Array<{ plotId: number; labourItemId: number }> = [];
    plotIds.forEach(plotId => {
      (labourItemsMap.get(plotId) ?? []).forEach(item => {
        if (item.allocationCount > 0) {
          items.push({ plotId, labourItemId: item.id });
        }
      });
    });
    return items;
  }, [plotIds, labourItemsMap]);

  const variationsWithContext = useMemo(() => {
    const items: Array<{ plotId: number; variationId: string }> = [];
    plotIds.forEach(plotId => {
      (variationsMap.get(plotId) ?? []).forEach(variation => {
        items.push({ plotId, variationId: variation.id });
      });
    });
    return items;
  }, [plotIds, variationsMap]);

  const labourAllocationQueries = useQueries({
    queries: bookedItemsWithContext.map(({ plotId, labourItemId }) => ({
      queryKey: tableAllocationsKeys.list(
        siteId,
        plotId,
        labourItemId,
        isAuthenticated
      ),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const result = unwrap(
          await api.legacy.GET(
            '/api/v2/sites/{siteId}/plots/{plotId}/labour-items/{labourItemId}/allocations',
            {
              params: {
                path: { siteId, plotId, labourItemId },
                query: { offset: 0, take: PAGE_SIZE },
              },
              signal,
            }
          )
        );
        return { labourItemId, allocations: result.items ?? [] };
      },
      enabled: isAuthenticated && siteId > 0 && plotId > 0 && labourItemId > 0,
      staleTime: 30000,
    })),
  });

  const variationAllocationQueries = useQueries({
    queries: variationsWithContext.map(({ plotId, variationId }) => ({
      queryKey: tableAllocationsKeys.variationList(
        siteId,
        plotId,
        variationId,
        isAuthenticated
      ),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const result = unwrap(
          await api.legacy.GET(
            '/api/v2/sites/{siteId}/plots/{plotId}/variations/{variationId}/allocations',
            {
              params: {
                path: { siteId, plotId, variationId },
                query: { offset: 0, take: PAGE_SIZE },
              },
              signal,
            }
          )
        );
        return { variationId, allocations: result.items ?? [] };
      },
      enabled: isAuthenticated && siteId > 0 && plotId > 0 && !!variationId,
      staleTime: 30000,
    })),
  });

  const labourAllocationsMap = useMemo(() => {
    const map = new Map<number, AllocationListItem[]>();
    labourAllocationQueries.forEach(query => {
      if (query.data) {
        map.set(query.data.labourItemId, query.data.allocations);
      }
    });
    return map;
  }, [labourAllocationQueries]);

  const variationAllocationsMap = useMemo(() => {
    const map = new Map<string, AllocationListItem[]>();
    variationAllocationQueries.forEach(query => {
      if (query.data) {
        map.set(query.data.variationId, query.data.allocations);
      }
    });
    return map;
  }, [variationAllocationQueries]);

  const plotsWithNested: PlotLedgerSource[] = useMemo(
    () =>
      plots.map(plot => ({
        plot,
        isExpanded: expandedPlots.has(plot.id),
        labourItems: labourItemsMap.get(plot.id),
        variations: variationsMap.get(plot.id),
        allocationsByLabourItem: labourAllocationsMap,
        allocationsByVariation: variationAllocationsMap,
      })),
    [
      plots,
      expandedPlots,
      labourItemsMap,
      variationsMap,
      labourAllocationsMap,
      variationAllocationsMap,
    ]
  );

  const isLoadingAnyNested =
    labourItemQueries.some(q => q.isLoading) ||
    variationQueries.some(q => q.isLoading) ||
    labourAllocationQueries.some(q => q.isLoading) ||
    variationAllocationQueries.some(q => q.isLoading);

  return {
    plotsWithNested,
    isLoadingAnyNested,
  };
}
